/** 将 parseImageContainerDeployConfig 解析结果写入 MaaS 创建表单 */
import type { FormInstance } from '@arco-design/web-react/es/Form';

import type { CommonImageItemType } from '@/components-biz/ImageSelect';
import {
  applyHealthCheckFromDeployConfig,
  clearHealthCheckFromForm,
  hasMeaningfulHealthCheckConfig,
  parseHealthCheckFromDeployConfig,
} from '@/delivery-type-plugin/capabilities/health-check';
import { HEALTH_CHECK_FIELD } from '@/delivery-type-plugin/capabilities/health-check/fieldKeys';
import type { MaasSharedFormValues } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import { parseFormJsonField, toFormJsonString } from '@/delivery-type-plugin/capabilities/maas-init/utils/jsonFormField';
import { syncPrimaryDataVolumeMountPath } from '@/delivery-type-plugin/capabilities/maas-node-groups/masterGroupForm';
import { INFERENCE_SERVICE_FIELD, INFERENCE_SYSTEM_FIELD } from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeFieldNames';
import { disableSkuModulesForPreset, enableSkuModulesForPreset } from '@/delivery-type-plugin/platform/scene-layout';

import type { ContainerDeployEnvVarItem } from './containerDeployConfigCore';
import {
  IMAGE_CONTAINER_DEPLOY_INSTANCE_SCHEDULING_FIELD,
  IMAGE_CONTAINER_DEPLOY_MOUNT_PATH_FIELD,
  IMAGE_CONTAINER_DEPLOY_STARTUP_FIELD,
} from './containerDeployConfigFields';
import {
  parseContainerDeployConfigObject,
  parseImageContainerDeployConfig,
  resolveContainerDeployConfigFromFormValues,
  syncInferenceSystemWithImageContainerDeployConfig,
} from './deployConfigPayload';

const INFERENCE_SYSTEM_IMAGE_FIELDS = [
  'image',
  'url',
  'imageName',
  'imageKey',
  'architecture',
  'type',
  'os',
  'version',
  'bootloader',
] as const;

/** 合并 hidden JSON 与点路径字段，得到最新的推理 system */
function readInferenceSystemFromForm(form: FormInstance): Record<string, unknown> | undefined {
  const raw = form.getFieldValue(INFERENCE_SYSTEM_FIELD);
  let system = parseFormJsonField<Record<string, unknown>>(raw);
  if (!system && raw && typeof raw === 'object' && !Array.isArray(raw)) {
    system = { ...(raw as Record<string, unknown>) };
  }

  const patch: Record<string, unknown> = {};
  for (const key of INFERENCE_SYSTEM_IMAGE_FIELDS) {
    const value = form.getFieldValue(`${INFERENCE_SYSTEM_FIELD}.${key}`);
    if (value !== undefined && value !== null && value !== '') {
      patch[key] = value;
    }
  }

  if (!system && !Object.keys(patch).length) return undefined;
  return { ...(system || {}), ...patch };
}

/** 仅更新 security 子字段，避免覆盖 _inferenceSystem 整对象导致 imageKey 选中态丢失 */
function writeInferenceSystemSecurityToForm(form: FormInstance, security?: Record<string, unknown>) {
  const field = `${INFERENCE_SYSTEM_FIELD}.security`;
  if (security && Object.keys(security).length) {
    form.setFieldValue(field, security);
    return;
  }
  form.setFieldValue(field, undefined);
}

function syncInferenceSystemSecurityOnForm(form: FormInstance, imageItem?: CommonImageItemType | null) {
  const inferenceSystem = readInferenceSystemFromForm(form);
  const syncedSystem = syncInferenceSystemWithImageContainerDeployConfig(inferenceSystem, imageItem);
  writeInferenceSystemSecurityToForm(form, syncedSystem?.security as Record<string, unknown> | undefined);
}

export function resolveImageContainerDeployStartupFromFormValues(
  values: Pick<MaasSharedFormValues, typeof IMAGE_CONTAINER_DEPLOY_STARTUP_FIELD | 'inferenceFrameworkImage'>,
): ContainerDeployStartupCommands | undefined {
  const direct = parseFormJsonField<ContainerDeployStartupCommands>(values[IMAGE_CONTAINER_DEPLOY_STARTUP_FIELD]);
  if (direct && Object.keys(direct).length > 0) {
    return direct;
  }
  const config = resolveContainerDeployConfigFromFormValues(values);
  const startup = config?.startupCommands;
  if (startup && typeof startup === 'object' && Object.keys(startup).length > 0) {
    return startup as ContainerDeployStartupCommands;
  }
  return undefined;
}

export function hasStartupCommandsContent(startup?: ContainerDeployStartupCommands): boolean {
  if (!startup || typeof startup !== 'object') return false;
  const checkItem = (item?: ContainerDeployStartupCommandItem) => {
    const args = item?.args;
    return !!(item?.command?.trim() || (Array.isArray(args) && args.length > 0));
  };
  return (
    checkItem(startup.normal) ||
    checkItem(startup.single) ||
    checkItem(startup.distributed) ||
    checkItem(startup.primarySecondary?.master) ||
    checkItem(startup.primarySecondary?.worker)
  );
}

/** 制品启动命令参数定义（与 accessConfig.startupCommands.args 一致） */
export interface ContainerDeployStartupArgDefinition {
  name: string;
  description?: string;
  defaultValue?: string;
  optional?: boolean;
  required: boolean;
  editable: boolean;
  visible: boolean;
}

/** 节点弹窗启动参数表单行 */
export interface StartupArgFormRow {
  name: string;
  value: string;
  _self?: string;
}

/** 启动参数 API 存储格式：有值时为 name=value，无值时仅 name（不带 =） */
export function formatStartupArgToken(name: string, value: string): string | null {
  const n = String(name ?? '').trim();
  const v = String(value ?? '').trim();
  if (!n && !v) return null;
  if (!n) return v;
  if (!v) return n;
  return `${n}=${v}`;
}

/** 解析 API / 节点 JSON 中的启动参数 token（支持 name=value、name 无值、旧版仅 value） */
export function parseStartupArgToken(token: string): { name: string; value: string } {
  const trimmed = String(token ?? '').trim();
  if (!trimmed) return { name: '', value: '' };
  const eq = trimmed.indexOf('=');
  if (eq === -1) return { name: trimmed, value: '' };
  const name = trimmed.slice(0, eq);
  const value = trimmed.slice(eq + 1);
  if (name) return { name, value };
  return { name: '', value };
}

/** 表单行转 API 启动参数数组 */
export function startupArgFormRowsToApiTokens(rows: StartupArgFormRow[]): string[] {
  return rows
    .map((row) => formatStartupArgToken(String(row?.name ?? ''), String(row?.value ?? '')))
    .filter((token): token is string => !!token);
}

export interface ContainerDeployStartupCommandItem {
  command?: string;
  args?: ContainerDeployStartupArgDefinition[] | string[] | string;
}

export interface ContainerDeployStartupCommands {
  modes?: string[];
  normal?: ContainerDeployStartupCommandItem;
  single?: ContainerDeployStartupCommandItem;
  distributed?: ContainerDeployStartupCommandItem;
  primarySecondary?: {
    master?: ContainerDeployStartupCommandItem;
    worker?: ContainerDeployStartupCommandItem;
  };
}

/** 模型挂载路径：persistentVolumeMountPath + / + 模型名称 */
export function buildModelMountPathFromDeployConfig(
  persistentVolumeMountPath: string | undefined,
  modelName: string | undefined,
): string | undefined {
  const base = persistentVolumeMountPath ? persistentVolumeMountPath.trim().replace(/\/+$/, '') : '';
  const name = modelName ? modelName.trim().replace(/^\/+/, '') : '';
  if (!base || !name) return undefined;
  return `${base}/${name}`;
}

export interface ApplyImageContainerDeployConfigOptions {
  setModelMountPath?: boolean;
  modelName?: string;
  /** 是否写入镜像 startupCommands 到 hidden 字段，模型实例传 false */
  includeStartupCommands?: boolean;
  /** 是否写入镜像 apis 到服务字段；embedded 节点抽屉传 false */
  includeServices?: boolean;
  /** 服务列表写入字段，默认 _inferenceService */
  serviceField?: string;
  /** 探活写入字段，默认 _containerDeployHealthCheck */
  healthCheckField?: string;
  /** 共享内存写入字段，默认 _containerDeploySharedMemory */
  sharedMemoryField?: string;
  /** 服务/探活/共享内存目标表单（embedded 抽屉写入 drawer form） */
  serviceTargetForm?: FormInstance;
  healthCheckTargetForm?: FormInstance;
  sharedMemoryTargetForm?: FormInstance;
  /** 是否根据镜像预填/重置高级配置；训练集群传 false */
  prefillAdvancedConfig?: boolean;
}

export interface ClearImageContainerDeployConfigOptions {
  includeServices?: boolean;
  prefillAdvancedConfig?: boolean;
  healthCheckField?: string;
  sharedMemoryField?: string;
  healthCheckTargetForm?: FormInstance;
  sharedMemoryTargetForm?: FormInstance;
}

/** 选中镜像后，将 containerDeployConfig 映射到表单字段 */
export function applyImageContainerDeployConfigToForm(
  form: FormInstance,
  imageItem: CommonImageItemType,
  options?: ApplyImageContainerDeployConfigOptions,
) {
  const includeStartupCommands = options?.includeStartupCommands !== false;
  const includeServices = options?.includeServices !== false;
  const serviceField = options?.serviceField ?? INFERENCE_SERVICE_FIELD;
  const serviceForm = options?.serviceTargetForm ?? form;
  const healthCheckForm = options?.healthCheckTargetForm ?? form;
  const healthCheckField = options?.healthCheckField ?? HEALTH_CHECK_FIELD;
  const prefillAdvancedConfig = options?.prefillAdvancedConfig !== false;
  const { services, persistentVolumeMountPath, persistentVolumeMountPaths, envVars, startupCommands, instanceScheduling } =
    parseImageContainerDeployConfig(imageItem);

  if (includeServices) {
    if (services?.length) {
      serviceForm.setFieldValue(serviceField, toFormJsonString(services));
    } else {
      serviceForm.setFieldValue(serviceField, undefined);
    }
  }

  const mountPaths = persistentVolumeMountPaths?.length
    ? persistentVolumeMountPaths
    : String(persistentVolumeMountPath ?? '').trim()
      ? [String(persistentVolumeMountPath).trim()]
      : [];
  const mountBase = mountPaths[0];
  if (mountBase) {
    form.setFieldValue(IMAGE_CONTAINER_DEPLOY_MOUNT_PATH_FIELD, mountBase);
  } else {
    form.setFieldValue(IMAGE_CONTAINER_DEPLOY_MOUNT_PATH_FIELD, undefined);
  }

  if (options?.setModelMountPath) {
    const modelName = String(options.modelName ?? form.getFieldValue('model.name') ?? '').trim();
    const mountPath = buildModelMountPathFromDeployConfig(mountBase || undefined, modelName);
    if (mountPath) {
      form.setFieldValue('model.mountPath', mountPath);
      syncPrimaryDataVolumeMountPath(form, mountPath);
    }
  } else if (mountBase) {
    form.setFieldValue('dataMountPath', mountBase);
  }

  const commonEnv = (envVars || [])
    .filter((item) => String(item?.key ?? '').trim())
    .map((item: ContainerDeployEnvVarItem) => ({
      name: String(item.key).trim(),
      value: item.value ?? '',
      _self: JSON.stringify(item),
    }));
  if (commonEnv.length) {
    form.setFieldValue('commonEnv', commonEnv);
  } else {
    form.setFieldValue('commonEnv', undefined);
  }

  if (includeStartupCommands) {
    const startup = startupCommands as ContainerDeployStartupCommands;
    if (hasStartupCommandsContent(startup)) {
      form.setFieldValue(IMAGE_CONTAINER_DEPLOY_STARTUP_FIELD, toFormJsonString(startup));
    } else {
      form.setFieldValue(IMAGE_CONTAINER_DEPLOY_STARTUP_FIELD, undefined);
    }
  }

  if (instanceScheduling) {
    form.setFieldValue(IMAGE_CONTAINER_DEPLOY_INSTANCE_SCHEDULING_FIELD, toFormJsonString(instanceScheduling));
  } else {
    form.setFieldValue(IMAGE_CONTAINER_DEPLOY_INSTANCE_SCHEDULING_FIELD, undefined);
  }

  syncInferenceSystemSecurityOnForm(form, imageItem);

  const fullConfig = imageItem?.containerDeployConfig ? parseContainerDeployConfigObject(imageItem.containerDeployConfig) : undefined;
  if (prefillAdvancedConfig) {
    applyHealthCheckFromDeployConfig(healthCheckForm, fullConfig, { field: healthCheckField });
    const healthCheckValues = parseHealthCheckFromDeployConfig(fullConfig?.healthCheck as Record<string, unknown> | undefined);
    healthCheckForm.setFieldValue('_healthCheckEnabled', hasMeaningfulHealthCheckConfig(healthCheckValues));
  }

  /** 对齐 CI：有预置数据时自动勾选；无预填环境变量时取消 envVars（不影响 App 的 appService） */
  const modulesToEnable: string[] = [];
  if (prefillAdvancedConfig) {
    const healthCheckValues = parseHealthCheckFromDeployConfig(fullConfig?.healthCheck as Record<string, unknown> | undefined);
    if (healthCheckValues?.enabled === true) {
      modulesToEnable.push('healthCheck');
    } else {
      disableSkuModulesForPreset(form, ['healthCheck']);
    }
  }
  if (includeServices && services?.length) {
    modulesToEnable.push('service', 'appService');
  }
  if (commonEnv.length) {
    modulesToEnable.push('envVars', 'appService');
  } else {
    disableSkuModulesForPreset(form, ['envVars']);
  }
  if (includeStartupCommands && hasStartupCommandsContent(startupCommands as ContainerDeployStartupCommands)) {
    modulesToEnable.push('appService');
  }
  enableSkuModulesForPreset(form, [...new Set(modulesToEnable)]);
}

/** 切换/清空推理镜像时，移除由镜像 containerDeployConfig 写入的表单字段 */
export function clearImageContainerDeployConfigFromForm(form: FormInstance, options?: ClearImageContainerDeployConfigOptions) {
  const includeServices = options?.includeServices !== false;
  const prefillAdvancedConfig = options?.prefillAdvancedConfig !== false;
  const healthCheckForm = options?.healthCheckTargetForm ?? form;
  const healthCheckField = options?.healthCheckField ?? HEALTH_CHECK_FIELD;
  if (includeServices) {
    form.setFieldValue(INFERENCE_SERVICE_FIELD, undefined);
  }
  form.setFieldValue(IMAGE_CONTAINER_DEPLOY_MOUNT_PATH_FIELD, undefined);
  form.setFieldValue(IMAGE_CONTAINER_DEPLOY_STARTUP_FIELD, undefined);
  form.setFieldValue(IMAGE_CONTAINER_DEPLOY_INSTANCE_SCHEDULING_FIELD, undefined);
  form.setFieldValue('commonEnv', undefined);
  disableSkuModulesForPreset(form, ['envVars']);
  if (prefillAdvancedConfig) {
    clearHealthCheckFromForm(healthCheckForm, { field: healthCheckField });
    healthCheckForm.setFieldValue('_healthCheckEnabled', false);
  }

  syncInferenceSystemSecurityOnForm(form, null);

  form.setFieldValue('inferenceFrameworkImage', undefined);
}
